import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faSignOut, faSignIn, faBell, faEnvelope, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { useAuthAdapter } from '../hooks/useAuthAdapter';
import { mobileAPI } from '../../api/MobileAPI';
import ClubAutocomplete from './controls/ClubAutocomplete';
import ThemeSelector from './ThemeSelector';
import PushSetup from './PushSetup';
const ROLES = ['Swimmer', 'Parent', 'Coach', 'Other'];
const buildDate = (() => {
    const d = new Date(Number(BUILD_VERSION));
    return [
        d.getFullYear(),
        String(d.getMonth() + 1).padStart(2, '0'),
        String(d.getDate()).padStart(2, '0'),
        String(d.getHours()).padStart(2, '0'),
        String(d.getMinutes()).padStart(2, '0'),
    ].join('.');
})();
const ProfileSettings = ({ className = '', unreadCount = 0, onOpenNotifications, onOpenContact, onOpenAbout, onShowToast }) => {
    const { isSignedIn, user, signIn, signOut } = useAuthAdapter();
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef(null);
    const [homeClub, setHomeClub] = useState('');
    const [role, setRole] = useState('');
    const [profileLoaded, setProfileLoaded] = useState(false);
    useEffect(() => {
        if (!open)
            return;
        const handler = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);
    useEffect(() => {
        if (!open)
            return;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [open]);
    useEffect(() => {
        if (!open || !isSignedIn || profileLoaded)
            return;
        mobileAPI.getProfile()
            .then(profile => {
            setHomeClub(profile.homeClub ?? '');
            setRole(profile.role ?? '');
            setProfileLoaded(true);
        })
            .catch(err => console.warn('[profile] failed to load profile:', err));
    }, [open, isSignedIn, profileLoaded]);
    const saveProfile = async (next) => {
        try {
            await mobileAPI.saveProfile({
                homeClub: next.homeClub || undefined,
                role: next.role || undefined,
            });
        }
        catch (err) {
            console.error('[profile] failed to save profile:', err);
            onShowToast?.('Failed to save profile', 'error');
        }
    };
    const handleHomeClubCommit = () => {
        void saveProfile({ homeClub, role });
    };
    const handleRoleChange = (next) => {
        setRole(next);
        void saveProfile({ homeClub, role: next });
    };
    const handleSignIn = () => {
        void signIn();
    };
    const handleSignOut = () => {
        onShowToast?.('Successfully signed out', 'success');
        void signOut();
    };
    return (React.createElement("div", { className: `profile-settings ${className}`, ref: wrapperRef },
        React.createElement("button", { type: "button", className: "btn-ghost profile-settings-trigger", onClick: () => setOpen(o => !o), "aria-label": "Profile and settings", "aria-expanded": open, title: `Profile & Settings${unreadCount > 0 ? ` - ${unreadCount} unread notifications` : ''}`, style: { color: 'var(--primary)' } },
            React.createElement("span", { style: { position: 'relative', display: 'inline-block' } },
                React.createElement(FontAwesomeIcon, { icon: faUser }),
                unreadCount > 0 &&
                    React.createElement(FontAwesomeIcon, { className: "hide-on-mobile", icon: faBell, style: {
                            color: 'var(--danger)',
                            width: '11px',
                            height: '11px',
                            position: 'absolute',
                            top: '-3px',
                            right: '-3px'
                        } }))),
        open && (React.createElement("div", { className: "profile-settings-popover", role: "menu" },
            React.createElement("div", { className: "profile-settings-header" }, isSignedIn ? (React.createElement("div", { className: "profile-info" },
                React.createElement(FontAwesomeIcon, { icon: faUser, className: "profile-icon" }),
                React.createElement("div", { className: "profile-text" },
                    React.createElement("div", { className: "profile-name" }, user?.name ?? 'Signed in'),
                    React.createElement("div", { className: "profile-status" }, "Signed in")))) : (React.createElement("div", { className: "profile-info" },
                React.createElement(FontAwesomeIcon, { icon: faUser, className: "profile-icon" }),
                React.createElement("div", { className: "profile-text" },
                    React.createElement("div", { className: "profile-status" }, "Not signed in"))))),
            React.createElement("div", { className: "profile-settings-divider" }),
            React.createElement("div", { className: "profile-settings-scroll" },
                isSignedIn && (React.createElement("div", { className: "profile-settings-section profile-settings-fields" },
                    React.createElement("div", { className: "profile-settings-field" },
                        React.createElement("label", { className: "profile-settings-label" }, "Home Club"),
                        React.createElement(ClubAutocomplete, { inputClassName: "profile-settings-input", value: homeClub, onChange: setHomeClub, onCommit: handleHomeClubCommit, placeholder: "Start typing your club name" })),
                    React.createElement("div", { className: "profile-settings-field" },
                        React.createElement("label", { className: "profile-settings-label" }, "Role"),
                        React.createElement("select", { className: "profile-settings-input", value: role, onChange: e => handleRoleChange(e.target.value) },
                            React.createElement("option", { value: "", disabled: true }, "Select a role"),
                            ROLES.map(r => (React.createElement("option", { key: r, value: r }, r))))))),
                React.createElement("div", { className: "profile-settings-section" },
                    React.createElement("label", { className: "profile-settings-label" }, "Theme"),
                    React.createElement(ThemeSelector, null)),
                React.createElement("div", { className: "profile-settings-section" },
                    React.createElement("label", { className: "profile-settings-label" },
                        "Notifications",
                        unreadCount > 0 &&
                            React.createElement(FontAwesomeIcon, { icon: faBell, style: {
                                    color: 'var(--danger)',
                                    width: '11px',
                                    height: '11px',
                                    margin: '3px 7px'
                                } })),
                    React.createElement(PushSetup, { unreadCount: unreadCount, onOpenNotifications: onOpenNotifications ? () => { onOpenNotifications(); setOpen(false); } : undefined })),
                React.createElement("div", { className: "profile-settings-section" },
                    React.createElement("label", { className: "profile-settings-label" }, "Pages"),
                    (onOpenContact || onOpenAbout) && (React.createElement("div", { className: "mobile-supplement" },
                        onOpenAbout && (React.createElement("button", { type: "button", role: "menuItem", className: "profile-settings-action", onClick: () => { onOpenAbout(); setOpen(false); } },
                            React.createElement(FontAwesomeIcon, { icon: faCircleInfo }),
                            React.createElement("span", null, "About"))),
                        onOpenContact && (React.createElement("button", { type: "button", role: "menuItem", className: "profile-settings-action", onClick: () => { onOpenContact(); setOpen(false); } },
                            React.createElement(FontAwesomeIcon, { icon: faEnvelope }),
                            React.createElement("span", null, "Contact"))))))),
            React.createElement("div", { className: "profile-settings-divider" }),
            React.createElement("button", { type: "button", role: "menuitem", className: "profile-settings-action", onClick: () => {
                    if (isSignedIn) {
                        handleSignOut();
                    }
                    else {
                        handleSignIn();
                    }
                    setOpen(false);
                } },
                React.createElement(FontAwesomeIcon, { icon: isSignedIn ? faSignOut : faSignIn }),
                React.createElement("span", null, isSignedIn ? 'Sign Out' : 'Sign In')),
            React.createElement("div", { className: "profile-build-version" },
                "Build ",
                buildDate)))));
};
export default ProfileSettings;
